import { useEffect, useMemo, useState } from 'react'
import { SlideOver } from './SlideOver.js'
import { fetchTemplates, importSupplierQuote, previewSupplierQuote, type SupplierPreview, type SupplierPreviewLine, type TemplateRow } from '../lib/api.js'
import { DEFAULT_IMPORT_MARGIN_PCT, marginOnSell, sellFromCost } from '@shared/supplierQuote'
import { formatUKDate } from '@shared/dates'

/**
 * Decision 211 — "New quote from a distributor quote". Upload Ingram's
 * workbook, pick the margin, check the lines, create the quote.
 *
 * Two server calls: PREVIEW (parse + catalogue match, nothing written) and
 * IMPORT (the same file again, with the margin and any per-line changes).
 * Everything the user can change lives in `overrides`, keyed by the sheet's
 * own line ref, so the preview and the import see the same instructions.
 */

type Override = { qty?: number | null; sellPrice?: number | null; include?: boolean }

const gbp = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export function SupplierQuoteImport({ onBack, onClose, onCreated, onError }: {
  onBack: () => void
  onClose: () => void
  onCreated: (quoteId: string, notice: string | null) => void
  onError: (message: string) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [marginPct, setMarginPct] = useState<string>(String(DEFAULT_IMPORT_MARGIN_PCT))
  const [reference, setReference] = useState('')
  const [preview, setPreview] = useState<SupplierPreview | null>(null)
  const [overrides, setOverrides] = useState<Record<string, Override>>({})
  const [busy, setBusy] = useState<'preview' | 'import' | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  // Decision 211b: optionally start from a template — its sections for
  // Wave9's own services come pre-populated; the distributor's section goes
  // first or last among them.
  const [templates, setTemplates] = useState<TemplateRow[]>([])
  const [templateId, setTemplateId] = useState('')
  const [placement, setPlacement] = useState<'first' | 'last'>('first')
  useEffect(() => {
    fetchTemplates()
      .then((r) => setTemplates(r.templates.filter((t) => t.active).sort((a, b) => Number(a.partnerId !== null) - Number(b.partnerId !== null) || a.name.localeCompare(b.name))))
      .catch(() => setTemplates([]))
  }, [])
  const template = templates.find((t) => t.id === templateId) ?? null

  const margin = Number(marginPct)
  const marginOk = Number.isFinite(margin) && margin >= 0 && margin < 100

  // The preview's sell prices were computed at the margin it was asked for;
  // a later change to the margin box re-prices locally so the table keeps up
  // without another upload (the import sends the margin again anyway).
  const lines = useMemo(() => {
    if (!preview) return []
    return preview.lines.map((l) => {
      const o = overrides[l.lineRef] ?? {}
      const qty = o.qty != null && o.qty > 0 ? o.qty : l.qty
      const sell = o.sellPrice != null ? o.sellPrice : (marginOk ? sellFromCost(l.unitCost, margin) : l.sellPrice)
      return { ...l, qty, sellPrice: sell, include: o.include !== false }
    })
  }, [preview, overrides, margin, marginOk])

  const totals = useMemo(() => {
    const inc = lines.filter((l) => l.include)
    const cost = inc.reduce((s, l) => s + l.qty * l.unitCost, 0)
    const sell = inc.reduce((s, l) => s + l.qty * l.sellPrice, 0)
    return { cost, sell, marginPct: marginOnSell(cost, sell), toCreate: inc.filter((l) => l.status === 'new').length, count: inc.length }
  }, [lines])

  const overrideList = () => Object.entries(overrides).map(([lineRef, o]) => ({ lineRef, ...o }))

  const runPreview = async () => {
    if (!file) { setProblem('Choose the distributor quote (.xlsx) first.'); return }
    if (!marginOk) { setProblem('Margin must be from 0% up to (not including) 100%.'); return }
    setBusy('preview'); setProblem(null)
    try {
      const p = await previewSupplierQuote(file, margin, overrideList())
      setPreview(p)
      if (p.header.reference) setReference(p.header.reference)
    } catch (e) { setProblem((e as Error).message) } finally { setBusy(null) }
  }

  const runImport = async () => {
    if (!file || !preview) return
    if (!marginOk) { setProblem('Margin must be from 0% up to (not including) 100%.'); return }
    if (!reference.trim()) { setProblem('The sheet carries no quote reference — type one.'); return }
    if (totals.count === 0) { setProblem('Every line is excluded — nothing to import.'); return }
    if (totals.toCreate > 0 && preview.writesDisabled) { setProblem('HubSpot writes are off on this deploy — the new SKUs cannot be created.'); return }
    const ok = window.confirm(
      `Create a draft quote from ${preview.header.distributor} quote ${reference.trim()}?\n\n`
      + `• ${totals.count} line(s) at ${margin}% margin — sell ${gbp(totals.sell)} on cost ${gbp(totals.cost)}\n`
      + (totals.toCreate > 0 ? `• ${totals.toCreate} new product(s) will be created in HubSpot (cloned from MS150-24MP-4X)\n` : '')
      + (template ? `• starts from template “${template.name}” — its sections come pre-populated, the distributor section ${placement === 'first' ? 'first' : 'after them'}\n` : '')
      + `• the workbook is saved with the quote and logged as cost evidence`,
    )
    if (!ok) return
    setBusy('import'); setProblem(null)
    try {
      const r = await importSupplierQuote(file, margin, overrideList(), reference.trim(), template ? { templateId: template.id, placement } : null)
      const notes: string[] = []
      if (r.productsCreated.length > 0) notes.push(`created ${r.productsCreated.length} product(s) in HubSpot: ${r.productsCreated.join(', ')}`)
      for (const w of r.warnings) notes.push(w)
      // The builder opens next — anything that needs a human's eye is said here.
      if (notes.length > 0) window.alert(`${r.quoteNumber} created from ${preview.header.distributor} quote ${r.reference} — please review:\n\n• ${notes.join('\n• ')}`)
      onCreated(r.quoteId, `Created ${r.quoteNumber} from ${preview.header.distributor} quote ${r.reference}${r.templateName ? ` on template “${r.templateName}”` : ''} — ${r.linesImported} line(s) at ${margin}% margin${r.productsCreated.length > 0 ? `, ${r.productsCreated.length} product(s) created in HubSpot` : ''}.`)
    } catch (e) { onError((e as Error).message); setBusy(null) }
  }

  const setOv = (lineRef: string, patch: Override) => setOverrides((cur) => ({ ...cur, [lineRef]: { ...(cur[lineRef] ?? {}), ...patch } }))

  const statusPill = (l: SupplierPreviewLine) => {
    if (l.status === 'new') return <span className="pill pill--warn" title="Not in the HubSpot price book — will be created, cloned from MS150-24MP-4X">new product</span>
    if (l.status === 'inactive') return <span className="pill pill--info" title="In the price book but withdrawn — imported as an ad-hoc line">inactive · ad-hoc</span>
    return <span className="pill pill--ok" title={l.catalogueName ?? ''}>in catalogue</span>
  }

  return (
    <SlideOver title="New quote from a distributor quote" subtitle="Their unit price becomes our cost; you choose the margin. Missing SKUs are created in HubSpot." onClose={onClose} wide>
      <button className="btn btn--ghost btn--sm" onClick={onBack} style={{ marginBottom: 10 }}>← Back</button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'start' }}>
        <div className="field">
          <label htmlFor="supplier-file">Distributor quote (.xlsx)</label>
          <input id="supplier-file" className="input" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" data-testid="supplier-file" style={{ width: '100%' }}
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); setOverrides({}) }} />
        </div>
        <div className="field">
          <label htmlFor="supplier-margin">Margin on sell price %</label>
          <input id="supplier-margin" className="input" type="number" min={0} max={99.99} step={0.5} value={marginPct} data-testid="supplier-margin"
            onChange={(e) => setMarginPct(e.target.value)} style={{ width: 110 }} />
          <div className="hint">sell = cost ÷ (1 − margin)<br />30% on £2,397.11 → £3,424.44</div>
        </div>
      </div>
      <div className="field">
        <label htmlFor="supplier-template">Start from a template <span className="muted">(optional — its sections for Wave9 services come pre-populated)</span></label>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <select id="supplier-template" className="select" value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="supplier-template" style={{ maxWidth: 360 }}>
            <option value="">No template — the distributor section only</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.partnerId === null ? ' (Wave9 global)' : ''}</option>)}
          </select>
          {template && (
            <span style={{ display: 'inline-flex', gap: 10 }} data-testid="supplier-placement">
              <label><input type="radio" name="placement" checked={placement === 'first'} onChange={() => setPlacement('first')} /> Distributor section first</label>
              <label><input type="radio" name="placement" checked={placement === 'last'} onChange={() => setPlacement('last')} /> After the template's sections</label>
            </span>
          )}
        </div>
        {template && <div className="hint">{template.summary}{template.carries.label ? ` · carries ${template.carries.label}` : ''}{template.adhocLines > 0 ? ` · ${template.adhocLines} ad-hoc line(s) to review` : ''} — template lines are repriced from today's catalogue.</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <button className="btn" disabled={busy !== null || !file} onClick={() => void runPreview()} data-testid="supplier-preview">
          {busy === 'preview' ? 'Reading…' : preview ? 'Re-read the sheet' : 'Read the sheet'}
        </button>
        {preview && <span className="muted small">{preview.filename}</span>}
      </div>
      {problem && <div className="banner banner--warn" style={{ marginBottom: 10 }}>⚠ {problem}</div>}

      {preview && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 16px', marginBottom: 10 }} data-testid="supplier-header">
            <div><span className="muted small">Distributor</span><div>{preview.header.distributor}</div></div>
            <div>
              <span className="muted small">Quote reference</span>
              <div><input className="input" value={reference} onChange={(e) => setReference(e.target.value)} style={{ width: 180 }} data-testid="supplier-reference" /></div>
            </div>
            <div><span className="muted small">CCW reference</span><div>{preview.header.ccwReference ?? '—'}</div></div>
            <div><span className="muted small">Currency · rate</span><div>{preview.header.currency ?? '—'}{preview.header.exchangeRate ? ` · ${preview.header.exchangeRate}` : ''}</div></div>
            <div><span className="muted small">Price protection</span><div>{preview.header.priceProtectionText ?? '—'}</div></div>
            <div><span className="muted small">Quoted</span><div>{preview.header.quotedOn ? formatUKDate(preview.header.quotedOn) : '—'}{preview.header.salesperson ? ` · ${preview.header.salesperson}` : ''}</div></div>
          </div>
          {preview.warnings.map((w, i) => <div key={i} className="banner banner--warn" style={{ marginBottom: 6 }}>⚠ {w}</div>)}
          {preview.writesDisabled && totals.toCreate > 0 && (
            <div className="banner banner--warn" style={{ marginBottom: 6 }}>⚠ HubSpot writes are off on this deploy — {totals.toCreate} new SKU(s) cannot be created.</div>
          )}

          <div className="table-wrap">
            <table data-testid="supplier-lines">
              <thead>
                <tr><th></th><th>#</th><th>SKU</th><th>Description</th><th>Qty</th><th>Unit cost</th><th>Sell</th><th>Margin</th><th>Status</th></tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.lineRef} className={l.include ? undefined : 'muted'} data-testid={`supplier-line-${l.lineRef}`}>
                    <td><input type="checkbox" checked={l.include} onChange={(e) => setOv(l.lineRef, { include: e.target.checked })} aria-label={`Include ${l.sku}`} /></td>
                    <td className="muted small">{l.lineRef}</td>
                    <td><code>{l.sku}</code></td>
                    <td>
                      {l.description}
                      {l.parentLineRef && <div className="muted small">accessory of line {l.parentLineRef}</div>}
                      {l.serviceMonths ? <div className="muted small">{l.serviceMonths} months · resold one-off</div> : null}
                    </td>
                    <td><input className="input" type="number" min={0.01} step={1} value={l.qty} style={{ width: 70 }}
                      onChange={(e) => setOv(l.lineRef, { qty: Number(e.target.value) || null })} aria-label={`Quantity for ${l.sku}`} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {gbp(l.unitCost)}
                      {l.catalogueCost != null && Math.abs(l.catalogueCost - l.unitCost) >= 0.005 && (
                        <div className="muted small" title="The catalogue cost is not changed — the quote line carries this cost (logged as a cost variation).">catalogue {gbp(l.catalogueCost)}</div>
                      )}
                    </td>
                    <td><input className="input" type="number" min={0} step={0.01} value={l.sellPrice} style={{ width: 100 }}
                      onChange={(e) => setOv(l.lineRef, { sellPrice: e.target.value === '' ? null : Number(e.target.value) })} aria-label={`Sell price for ${l.sku}`} /></td>
                    <td className="muted small">{l.unitCost === 0 ? '—' : `${marginOnSell(l.unitCost, l.sellPrice) ?? 0}%`}</td>
                    <td>{statusPill(l)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}><strong>{totals.count} line(s)</strong>{totals.toCreate > 0 ? <span className="muted small"> · {totals.toCreate} to create in HubSpot</span> : null}</td>
                  <td><strong>{gbp(totals.cost)}</strong></td>
                  <td><strong>{gbp(totals.sell)}</strong></td>
                  <td>{totals.marginPct == null ? '—' : `${totals.marginPct}%`}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn btn--primary" disabled={busy !== null || totals.count === 0} onClick={() => void runImport()} data-testid="supplier-import">
              {busy === 'import' ? 'Creating…' : 'Create draft quote'}
            </button>
            <span className="muted small" style={{ alignSelf: 'center' }}>Add Wave9 services and adjust quantities in the builder afterwards.</span>
          </div>
        </>
      )}
    </SlideOver>
  )
}
